import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { getFirestoreDb } from '../services/firebase';
import { SEED_DOCUMENTATION } from '../services/seedDocumentation';

const CACHE_KEY = 'veez_docs_cache_v3';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 Hora de caché en cliente (Evita cobros y lecturas excesivas)

/**
 * Hook optimizado para documentación con Caché Local (localStorage) + Carga Única (getDocs)
 * Garantiza 0 lecturas innecesarias en Firestore y prevención total de errores de cuota/red.
 */
export function useFirestoreDocs(categoria) {
  const filterCategory = (data, cat) => {
    return cat ? data.filter((item) => item.categoria === cat) : data;
  };

  const getInitialDocs = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { timestamp, data } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS && Array.isArray(data) && data.length > 0) {
          return filterCategory(data, categoria);
        }
      }
    } catch (e) {
      // Ignorar errores de localStorage
    }
    return filterCategory(SEED_DOCUMENTATION, categoria);
  };

  const [docs, setDocs] = useState(getInitialDocs);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchDocsOptimized = async () => {
      try {
        // Verificar si la caché sigue vigente
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL_MS) {
            return; // 0 lecturas a Firestore (Ahorro de costos total)
          }
        }

        const db = getFirestoreDb();
        if (!db) return;

        const docsRef = collection(db, 'documentacion');
        const q = query(docsRef, orderBy('orden', 'asc'));

        const snapshot = await getDocs(q);
        if (snapshot && !snapshot.empty && isMounted) {
          const firestoreData = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data()
          }));

          // Guardar en caché local
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            timestamp: Date.now(),
            data: firestoreData
          }));

          setDocs(filterCategory(firestoreData, categoria));
        }
      } catch (err) {
        // Silenciar errores de lectura o cuotas de Firestore, usando el respaldo estático
        console.info('Usando repositorio local de documentación (ahorro de cuotas activo)');
      }
    };

    fetchDocsOptimized();

    return () => {
      isMounted = false;
    };
  }, [categoria]);

  return { docs, loading };
}
